// src/components/chat/ContactCustomerModal.js
// Contact marchand -> client : saisie du 1er message puis ouverture du fil.
// Crée la conversation côté serveur uniquement à l'envoi (pas de fil vide).
// Maxula

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiSend, FiMessageSquare, FiShield } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { chatAPI } from '../../services/api';

const BRAND = '#e11d7b';
const BRAND_HOVER = '#c71a6e';

export default function ContactCustomerModal({ order, onClose }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const customer = order?.customer || {};
  const customerId = customer.id || customer._id || order?.userId || null;
  const customerName =
    `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.email || 'Client';
  const deal = (order?.items || [])[0]?.deal || null;
  const dealId = deal?.id || null;
  const dealTitle = deal?.title || '';

  const send = async () => {
    const body = text.trim();
    if (!body || !customerId || sending) return;
    setSending(true);
    try {
      const res = await chatAPI.startAsMerchant({ customerId, dealId, text: body });
      const convId = res?.data?.data?.conversation?.id;
      toast.success('Message envoyé au client');
      onClose?.();
      navigate(convId ? `/merchant/messages?c=${convId}` : '/merchant/messages');
    } catch (e) {
      toast.error(e?.response?.data?.message || "Impossible d'envoyer le message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <FiMessageSquare style={{ color: BRAND }} className="shrink-0" />
            <h3 className="font-semibold text-gray-900 truncate">Contacter {customerName}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-gray-400 hover:text-gray-600 p-1 shrink-0"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Corps */}
        <div className="p-5 space-y-3">
          {dealTitle && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              À propos de : <span className="font-medium text-gray-700">{dealTitle}</span>
            </div>
          )}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                send();
              }
            }}
            rows={4}
            autoFocus
            placeholder="Écrivez votre message au client…"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 resize-none"
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 3px rgba(225,29,121,0.18)';
              e.target.style.borderColor = BRAND;
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.borderColor = '#E5E7EB';
            }}
          />
          <div className="flex items-start gap-1.5 text-[11px] text-gray-400">
            <FiShield size={12} className="shrink-0 mt-0.5" />
            <span>
              Pour la sécurité de tous, les numéros et emails sont masqués. Restez sur Maxula.
            </span>
          </div>
        </div>

        {/* Pied */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !text.trim() || !customerId}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = BRAND_HOVER;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = BRAND;
            }}
          >
            <FiSend size={15} /> {sending ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}
